import {
  ClipboardListIcon,
  CogIcon,
  DnaIcon,
  GraduationCapIcon,
  HospitalIcon,
  MicroscopeIcon,
  ScaleIcon,
  ShieldCheckIcon,
  WrenchIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  context: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Fundamentals of Surgical Instrumentation & Operating Room Practice: The Foundation',
    subtitle: 'The foundation your students need to understand everything else',
    context:
      'Your first introduction to surgical instrumentation can be transformative or overwhelming. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'Introduction to Surgical Instrumentation and Its Role in Healthcare',
      'History and Evolution of Surgical Instruments and Operating Room Practices',
      'Roles and Responsibilities of the Surgical Team',
      'Organization and Essential Areas of the Operating Room',
      'Basic Principles of Surgical Procedures and Perioperative Care',
      'Professional Communication and Teamwork',
      'Introduction to Patient Safety and Infection Prevention',
      'Essential Terminology and Foundational Concepts',
      'Classroom Explanations and Practical Educational Examples',
      'Review & Teacher Guidance',
    ],
    result:
      'Your students understand surgical instrumentation as a practical discipline that connects equipment operation, patient safety, and professional healthcare practice—not an impossible list of terms to memorize.',
    icon: WrenchIcon,
  },
  {
    number: '2',
    title: 'Surgical Instruments: Classification, Identification & Functions',
    subtitle: 'Everything your students need to know about surgical instrument types and applications',
    context:
      'This is where many students struggle to build the instrument identification foundation. This volume gives you the materials to help them truly understand surgical instruments.',
    items: [
      'Fundamental Principles of Surgical Instrument Identification',
      'Classification of Cutting and Dissecting Instruments',
      'Grasping and Holding Instruments',
      'Clamping and Occluding Instruments',
      'Retracting and Exposing Instruments',
      'Suturing and Tissue-Approximation Instruments',
      'Instrument Design, Structure, and Functional Characteristics',
      'Differences Between Commonly Used Surgical Instruments',
      'Instrument Selection Concepts for Different Surgical Situations',
      'Educational Examples and Instrument-Recognition Activities',
      '15 worked surgical instrument identification and classification examples',
    ],
    result: 'Your students learn the instrument classification principles that underpin all surgical instrumentation practice.',
    icon: CogIcon,
  },
  {
    number: '3',
    title: 'Aseptic Techniques, Sterility & Infection Prevention',
    subtitle: 'Explore how surgical asepsis protects patients and prevents infections',
    context:
      'Asepsis is critical but complex. This volume gives you everything needed so your students grasp sterile technique clearly.',
    items: [
      'Fundamental Principles of Surgical Asepsis',
      'Microorganisms and Infection Transmission',
      'Surgical Hand Hygiene and Preparation',
      'Sterile and Nonsterile Environments',
      'Sterile Field Preparation and Maintenance',
      'Principles of Surgical Gowning and Gloving',
      'Contamination Risks and Prevention Strategies',
      'Surgical Site Infection Prevention Concepts',
      'Aseptic Decision-Making and Safety Awareness',
      'Classroom Scenarios Involving Breaches of Sterile Technique',
      '15 worked aseptic technique and contamination prevention examples',
    ],
    result: 'Your students understand how aseptic techniques protect patients and maintain surgical sterility.',
    icon: ShieldCheckIcon,
  },
  {
    number: '4',
    title: 'Surgical Procedures & Perioperative Workflow',
    subtitle: 'Explore how surgical teams execute coordinated perioperative processes',
    context: 'Perioperative workflow is essential but complex. This volume makes these systems understandable.',
    items: [
      'Introduction to the Perioperative Process',
      'Preoperative Preparation and Patient Considerations',
      'Operating Room Setup and Organization',
      'Surgical Instrument Preparation and Arrangement',
      'Basic Phases of Surgical Procedures',
      'Instrument Handling and Passing Principles',
      'Surgical Counts and Accountability Concepts',
      'Communication During Surgical Procedures',
      'Postoperative Workflow and Room Turnover',
      'Educational Case Examples Involving Perioperative Situations',
      '12 worked perioperative workflow and team coordination examples',
    ],
    result:
      'Your students understand how perioperative teams coordinate surgical procedures and manage operating room workflow.',
    icon: ClipboardListIcon,
  },
  {
    number: '5',
    title: 'Surgical Anatomy & Medical Terminology for Educators',
    subtitle: 'Understand anatomical foundations and medical language for surgical education',
    context:
      'Surgical anatomy and terminology are foundational. This volume makes these concepts accessible and understandable.',
    items: [
      'Fundamental Principles of Human Anatomy',
      'Anatomical Positions, Planes, and Directional Terminology',
      'Overview of Major Body Systems',
      'Musculoskeletal Anatomy Relevant to Surgical Education',
      'Cardiovascular and Respiratory System Fundamentals',
      'Digestive and Urinary System Fundamentals',
      'Nervous System and Sensory Structures',
      'Surgical Terminology, Prefixes, Suffixes, and Abbreviations',
      'Relationships Between Anatomical Structures and Surgical Approaches',
      'Teaching Explanations and Terminology Exercises',
      '12 worked surgical anatomy and medical terminology examples',
    ],
    result:
      'Your students can explain anatomical relationships and use surgical terminology accurately in healthcare contexts.',
    icon: DnaIcon,
  },
  {
    number: '6',
    title: 'Surgical Instrument Processing, Sterilization & Safety',
    subtitle: 'Shows how surgical instruments are safely processed and sterilized',
    context:
      'Instrument processing is essential to patient safety. This volume makes these concepts clear and applicable.',
    items: [
      'Introduction to Surgical Instrument Reprocessing',
      'Instrument Transport and Handling After Procedures',
      'Cleaning and Decontamination Principles',
      'Inspection and Maintenance of Surgical Instruments',
      'Instrument Assembly and Preparation',
      'Packaging and Sterilization Fundamentals',
      'Common Sterilization Methods and Their Applications',
      'Sterilization Monitoring and Quality Assurance Concepts',
      'Instrument Storage and Handling',
      'Workplace Safety and Risk Prevention',
      'Educational Examples Involving Instrument-Processing Errors',
      '12 worked instrument processing and sterilization examples',
    ],
    result:
      'Your students understand how instruments are safely processed and can identify sterilization requirements and errors.',
    icon: MicroscopeIcon,
  },
  {
    number: '7',
    title: 'Specialized Surgical Instrumentation Across Medical Disciplines',
    subtitle: 'Explore how different surgical specialties use specialized instrumentation',
    context:
      'Specialized instrumentation is diverse and important. This volume develops discipline-specific knowledge.',
    items: [
      'Introduction to Specialized Surgical Instrumentation',
      'General Surgery Instruments and Their Applications',
      'Orthopedic Surgical Instrumentation',
      'Cardiovascular Surgical Instrumentation',
      'Neurosurgical Instruments and Specialized Functions',
      'Obstetric and Gynecological Instrumentation',
      'Urological Surgical Instrumentation',
      'Ophthalmic and Otolaryngological Instrumentation',
      'Minimally Invasive and Laparoscopic Instrumentation',
      'Differences Between Specialty-Specific Instrument Groups',
      'Educational Comparisons and Specialty-Based Classroom Scenarios',
      '12 worked specialized surgical instrumentation examples',
    ],
    result:
      'Your students can identify specialty-specific instruments and understand when each type is used in surgical practice.',
    icon: HospitalIcon,
  },
  {
    number: '8',
    title: 'Operating Room Management, Patient Safety & Professional Practice',
    subtitle: 'Integrates patient safety and professional responsibility into surgical practice',
    context:
      'OR management and patient safety are fundamental to modern healthcare. This volume develops responsible professionals.',
    items: [
      'Principles of Operating Room Organization',
      'Surgical Team Coordination and Communication',
      'Patient Safety and Risk Management',
      'Surgical Safety Protocols and Checklists',
      'Documentation and Accountability Principles',
      'Equipment Readiness and Operational Safety',
      'Prevention and Analysis of Common Perioperative Errors',
      'Ethical and Professional Responsibilities',
      'Quality Improvement in Surgical Environments',
      'Human Factors and Teamwork',
      'Classroom Discussions Involving Safety and Professional Judgment',
      '15 practical operating room management and patient safety case studies',
    ],
    result:
      'Your students understand that surgical instrumentation safety and professional practice are built on communication, teamwork, and continuous improvement.',
    icon: ScaleIcon,
  },
  {
    number: '9',
    title: 'Teaching Surgical Instrumentation: Methods, Activities & Educational Strategies',
    subtitle: 'Your complete guide as a surgical instrumentation education professional',
    context:
      'This volume gives you exactly what you need to plan, organize, and evaluate your entire year of surgical instrumentation teaching.',
    items: [
      'Principles of Surgical Instrumentation Education',
      'Learning Objectives and Curriculum Organization',
      'Strategies for Explaining Technical and Clinical Concepts',
      'Classroom Activities for Surgical Instrument Identification',
      'Teaching Asepsis, Sterilization, and Patient Safety',
      'Case-Based Learning and Problem-Solving Activities',
      'Student Participation and Collaborative Learning',
      'Written Assessment Strategies',
      'Approaches to Evaluating Conceptual Understanding',
      'Teaching Adaptations for Different Learning Levels',
      'Educational Feedback and Continuous Improvement',
      'Strategies for Connecting Theory with Supervised Professional Training',
      'Capstone Classroom Applications',
      '10 complete integrated surgical instrumentation projects',
    ],
    result:
      'You\'ll never wonder "what do I teach today?" again. Everything is planned and contextualized for maximum student learning.',
    icon: GraduationCapIcon,
  },
];
